import { Box, Card, CardContent, CardHeader, CardProps, Stack, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/system'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { AuthorizationType } from 'src/enums/auth'
import { GridActionButton } from './common/GridActionButton'
import { DynamicTextTypography, IDynamicTextTypographyProps } from './DynamicTextTypography'
import { Spinner } from './Spinner'

export interface ICardAction {
  label: string
  onClick: () => void
  icon: ReactNode
  testId?: string
  permissionBinaryValues?: number[]
  context?: React.Context<any> | null
  permission?: number
  disabled?: boolean
  style?: React.CSSProperties
}

interface IGenericCardMinimalVersion extends Omit<CardProps, 'actions' | 'sx'> {
  title?: string
  titleTypographyProps?: Omit<IDynamicTextTypographyProps, 'text'>
  useAutoTruncate?: boolean
  minHeight?: string | number
  maxHeight?: string | number
  minWidth?: string | number
  maxWidth?: string | number
  actions?: Array<ICardAction>
  backgroundImage?: string
  sx?: object
  isLoading?: boolean
}

export const GenericCardMinimalVersion = ({
  title,
  titleTypographyProps,
  useAutoTruncate = false,
  minHeight,
  maxHeight,
  minWidth,
  maxWidth,
  actions,
  backgroundImage,
  children,
  isLoading,
  sx,
  ...rest
}: IGenericCardMinimalVersion) => {
  const theme = useTheme()
  const titleRef = useRef<HTMLDivElement>(null)
  const [titleVariant, setTitleVariant] = useState<'h3' | 'h4' | 'h5' | 'h6'>('h3')

  useEffect(() => {
    if (title && titleRef.current) {
      const containerWidth = titleRef.current.offsetWidth
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      if (context) {
        context.font = `${theme.typography.h3.fontWeight} ${theme.typography.h3.fontSize} ${theme.typography.h3.fontFamily}`
        let textWidth = context.measureText(title).width

        if (textWidth <= containerWidth * 0.8) {
          setTitleVariant('h3')
        } else {
          context.font = `${theme.typography.h4.fontWeight} ${theme.typography.h4.fontSize} ${theme.typography.h4.fontFamily}`
          textWidth = context.measureText(title).width

          if (textWidth <= containerWidth * 0.8) {
            setTitleVariant('h4')
          } else {
            context.font = `${theme.typography.h5.fontWeight} ${theme.typography.h5.fontSize} ${theme.typography.h5.fontFamily}`
            textWidth = context.measureText(title).width

            if (textWidth <= containerWidth * 0.8) {
              setTitleVariant('h5')
            } else {
              setTitleVariant('h6')
            }
          }
        }
      }
    }
  }, [title, theme, titleRef.current?.offsetWidth])

  return (
    <StyledCard
      sx={{
        minHeight,
        maxHeight,
        minWidth,
        maxWidth,
        backgroundImage: backgroundImage,
        ...sx,
      }}
      elevation={3}
      {...rest}
    >
      {(title || actions) && (
        <CardHeader
          action={
            <Stack direction={'row'} className="pr-8-core">
              {actions?.map((item, index) => (
                <GridActionButton
                  key={index}
                  onClick={item.onClick}
                  data-test-id={item.testId}
                  toolTipTitle={item.label}
                  bgColor={theme.palette.lightGray.main}
                  height="36px"
                  width="36px"
                  buttonIcon={item.icon}
                  permission={item.permission}
                  permissionBinaryValues={item.permissionBinaryValues}
                  context={item.context}
                  authorizationType={AuthorizationType.Hidden}
                  disabled={item.disabled}
                  shadowWeight={0}
                  style={item.style}
                />
              ))}
            </Stack>
          }
          title={
            title && (
              <Box ref={titleRef} data-test-id="card-title">
                <DynamicTextTypography
                  maxLength={titleVariant === 'h6' ? 38 : undefined}
                  variant={titleVariant}
                  text={title}
                  color={theme.palette.primaryMain[theme.palette.mode]}
                  sx={{ whiteSpace: 'nowrap' }}
                  {...titleTypographyProps}
                />
              </Box>
            )
          }
        />
      )}
      <CardContent>
        {isLoading ? (
          <Stack direction={'column'} justifyContent={'center'} alignItems={'center'} minHeight={'100px'}>
            <Spinner />
          </Stack>
        ) : (
          children
        )}
      </CardContent>
    </StyledCard>
  )
}

export const StyledCard = styled(Card)(() => ({
  height: '100%',
  borderRadius: '4px',
  '& .MuiCardContent-root': {
    padding: '12px !important',
  },
  '& .MuiCardHeader-root': {
    padding: '12px',
    paddingBottom: '0px',
    alignItems: 'center',
  },
  '& .MuiCardHeader-content': {
    overflow: 'hidden',
    flex: '1 1 auto',
    minWidth: 0,
  },
  '& .MuiCardHeader-action': {
    flex: '0 0 auto',
    alignSelf: 'center',
    marginTop: 0,
    marginRight: 0,
  },
}))

export const StyledCardTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primaryDark[theme.palette.mode],
}))
